import React, { ReactNode } from 'react';
import { compose, withHandlers, lifecycle, withState } from 'recompose';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import RssFeedDetails from '../../components/RssFeedDetails/RssFeedDetails';
import RssFeedsTable from '../../components/RssFeedsTable/RssFeedsTable';
import { ADD_FEED_TEXT, DEFAULT_FEED_NAME } from '../../constants/texts';
import NewEntityButton from '../../../../components/NewEntityButton/NewEntityButton';
import RssFeedCRUDActions from '@mm/state/actions/RssFeedCRUDActions';
import { unselectRssFeed } from '@mm/state/actions/viewActions';
import * as routes from '../../../../../common/constants/routes';
import '../../../common/css/sharedPanel.scss';
import { StoreState } from '@mm/state/store-interfaces';
import { LifeCycleThis } from '@mm/utils';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { rssFeedSelector } from '@mm/state/modules/rssFeedTableState/selectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { InactiveBotAlert } from '../../../../components/InactiveBotAlert/InactiveBotAlert';

type OuterProps = {};

type StateAndHandlers = {
  requestedFeed: boolean;
  setRequestedFeed: (requestedFeed: boolean) => void;
};

type Handlers = {
  renderNewEntityButton: () => ReactNode;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  StateAndHandlers &
  RouterComponentProps &
  Handlers;

type Props = OuterProps & InnerProps;

const RssFeedsPanel = ({
  activePageId,
  match,
  createFeed,
  chatbotId,
}: Props) => {
  return (
    <div className="shared-panel">
      <InactiveBotAlert />

      <Switch>
        <Route
          exact
          path={match.url}
          render={() => {
            const entity = {
              name: DEFAULT_FEED_NAME,
              pageId: activePageId,
            };
            return (
              <RssFeedsTable createFeed={() => createFeed(chatbotId, entity)} />
            );
          }}
        />
        <Route
          path={BotBuilderRoutes.rssFeed.path}
          component={RssFeedDetails}
        />
      </Switch>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  feed: state.view.chatbotEditor.selectedRssFeed,
  entities: rssFeedSelector(state),
  chatbotId: activeChatbotIdSelector(state),
  activePageId: state.view.chatbotEditor.build.activePageId,
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>, ownProps) => ({
  createFeed: (botId: number, entity) => {
    dispatch(unselectRssFeed());
    dispatch(RssFeedCRUDActions.create(botId, entity));
    ownProps.setRequestedFeed(true);
  },
  deselectFeed: () => {
    dispatch(unselectRssFeed());
  },
  getFeedById: (botId: number, feedId: number) =>
    dispatch(RssFeedCRUDActions.retrieveById(botId, feedId)),
});

const renderNewEntityButton =
  ({ createFeed, entities }: Props) =>
  () => {
    if (!entities || !entities.length) {
      return null;
    }

    return (
      <NewEntityButton
        text={ADD_FEED_TEXT}
        name={DEFAULT_FEED_NAME}
        createEntity={createFeed}
      />
    );
  };

function componentWillReceiveProps(
  this: LifeCycleThis<Props>,
  nextProps: Props,
) {
  const {
    feed,
    history,
    match: {
      params: { chatbotId },
      isExact,
    },
    requestedFeed,
    setRequestedFeed,
  } = nextProps;

  if (requestedFeed && isExact && feed) {
    history.push(
      `${routes.CHATBOT_EDITOR}/${chatbotId}/rss-feeds/edit/${feed.id}`,
    );
    setRequestedFeed(false);
  }
}

const enhance = compose<InnerProps, OuterProps>(
  withState('requestedFeed', 'setRequestedFeed', false),
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle<Props, {}>({ componentWillReceiveProps }),
  withHandlers({ renderNewEntityButton }),
);

export default enhance(RssFeedsPanel);
